import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Workflow, Zap, TrendingDown, Clock, ArrowLeft, BarChart3 } from "lucide-react";
import axios from "axios";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import PageTransition from "@/components/PageTransition";
import { staggerContainer, staggerItem, fadeInUp, hoverScale } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_d04ef3c1-d7de-40d8-b6b3-0d99244d2801/artifacts/0hpd8r65_icon_markremoved%20backround.png";

const AGENT_COLORS = {
  "gpt-5.2": "#10b981", "gpt-4o": "#3b82f6", "gpt-4o-mini": "#60a5fa",
  "claude-sonnet": "#f59e0b", "claude-haiku": "#fbbf24",
  "gemini-3-flash": "#06b6d4", "gemini-3-pro": "#0891b2", "local": "#6b7280",
};

export default function SharedWorkflow() {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const response = await axios.get(`${API}/shared/${shareCode}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Shared workflow not found");
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
  }, [shareCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
          <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] w-96 text-center">
            <CardContent className="pt-8 pb-8">
              <Workflow className="w-16 h-16 mx-auto mb-4 text-red-400 opacity-50" />
              <h3 className="text-xl font-outfit font-semibold mb-2">Not Found</h3>
              <p className="text-[var(--qn-foreground-muted)] text-sm mb-6">{error}</p>
              <Button onClick={() => navigate("/")} variant="outline" className="border-[var(--qn-border)]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageTransition>
    );
  }

  const wf = data.workflow;
  const opts = data.optimizations || [];
  const bestOpt = opts[0];

  return (
    <PageTransition>
      <div className="min-h-screen bg-[var(--qn-background)]">
        <motion.header initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass border-b border-[var(--qn-border)]">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_ICON} alt="QNNEAL" className="h-10 w-10 logo-spin" />
              <span className="font-outfit font-bold text-lg">QNNEAL</span>
              <Badge className="bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] text-sm">Shared</Badge>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Button onClick={() => navigate("/login")} className="btn-primary" size="sm" data-testid="try-qnneal-btn">
                Try QNNEAL Free
              </Button>
            </div>
          </div>
        </motion.header>

        <main className="max-w-5xl mx-auto px-6 py-10">
          <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-8">
            {/* Workflow header */}
            <motion.div variants={staggerItem}>
              <h1 className="text-3xl font-outfit font-bold mb-2">{wf.name}</h1>
              <p className="text-[var(--qn-foreground-muted)]">
                Shared by {data.owner?.name || "Anonymous"} · {wf.tasks?.length || 0} tasks · v{wf.version || 1}
              </p>
            </motion.div>

            {/* Best optimization stats */}
            {bestOpt && (
              <motion.div variants={staggerItem} className="grid grid-cols-3 gap-4">
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardContent className="pt-6 text-center">
                    <TrendingDown className="w-8 h-8 mx-auto mb-2 text-[var(--qn-success)]" />
                    <div className="text-3xl font-mono font-bold text-[var(--qn-success)]">{bestOpt.cost_reduction_percent}%</div>
                    <div className="text-sm text-[var(--qn-foreground-muted)] mt-1">Cost Reduction</div>
                  </CardContent>
                </Card>
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardContent className="pt-6 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-[var(--qn-primary)]" />
                    <div className="text-3xl font-mono font-bold text-[var(--qn-primary)]">{bestOpt.time_reduction_percent}%</div>
                    <div className="text-sm text-[var(--qn-foreground-muted)] mt-1">Time Reduction</div>
                  </CardContent>
                </Card>
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardContent className="pt-6 text-center">
                    <Zap className="w-8 h-8 mx-auto mb-2 text-[var(--qn-secondary)]" />
                    <div className="text-3xl font-mono font-bold text-[var(--qn-secondary)]">{bestOpt.optimization_time_ms}ms</div>
                    <div className="text-sm text-[var(--qn-foreground-muted)] mt-1">Optimization Time</div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Task DAG */}
            <motion.div variants={staggerItem}>
              <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <Workflow className="w-5 h-5 text-[var(--qn-primary)]" />
                    Workflow Tasks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-3">
                    {(wf.tasks || []).map((task, i) => (
                      <motion.div
                        key={task.id || i}
                        variants={staggerItem}
                        className="card-dark rounded-lg p-4 border-l-4"
                        style={{ borderLeftColor: AGENT_COLORS[task.agent] || "#374151" }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-base">{task.name}</span>
                          <Badge className="text-xs" style={{ backgroundColor: (AGENT_COLORS[task.agent] || "#374151") + "20", color: AGENT_COLORS[task.agent] || "#9ca3af" }}>
                            {task.agent}
                          </Badge>
                        </div>
                        <div className="text-sm text-[var(--qn-foreground-subtle)]">
                          {task.estimated_tokens?.toLocaleString()} tokens
                          {task.dependencies?.length > 0 && ` · depends on: ${task.dependencies.join(", ")}`}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Optimization history */}
            {opts.length > 0 && (
              <motion.div variants={staggerItem}>
                <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <CardHeader>
                    <CardTitle className="font-outfit flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[var(--qn-secondary)]" />
                      Optimization History ({opts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {opts.map((opt) => (
                        <div key={opt.optimization_id} className="flex items-center justify-between card-dark rounded-lg p-3">
                          <div>
                            <div className="font-mono text-sm">{opt.optimization_id}</div>
                            <div className="text-sm text-[var(--qn-foreground-subtle)]">
                              {opt.solver_type} · {new Date(opt.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-mono text-[var(--qn-success)]">-{opt.cost_reduction_percent}%</span>
                            <span className="text-sm font-mono text-[var(--qn-primary)]">{opt.optimization_time_ms}ms</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* CTA */}
            <motion.div variants={staggerItem} className="text-center py-8">
              <h3 className="text-xl font-outfit font-semibold mb-3">Optimize your own workflows</h3>
              <p className="text-[var(--qn-foreground-muted)] mb-6">QNNEAL uses quantum annealing to find the optimal agent-to-task mapping.</p>
              <motion.div {...hoverScale}>
                <Button onClick={() => navigate("/login")} className="btn-primary" size="lg" data-testid="signup-cta-btn">
                  <Zap className="w-5 h-5 mr-2" />
                  Start Optimizing Free
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </PageTransition>
  );
}
